import { useState, useRef, useEffect, ReactNode, useMemo, useContext } from "react";
import { Play, Pause, SkipBack, SkipForward, Loader2, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { AudioPlayerContext, audioPlayerContext } from "@/contexts/audioPlayerContext";

interface AudioPlayerProps {
  onClose?: () => void;
  showSkipBackAndForward?: boolean;
}

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
    const [minimized, setMinimized] = useState(false);
  const [open, setOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string>(undefined);
  const [title, setTitle] = useState<string>(undefined);
  const [playbackRate, setPlaybackRate] = useState<number>(1);

  const value = useMemo<AudioPlayerContext>(() => ({
    minimized,
    open,
    audioUrl: audioUrl,
    title,
    isPlaying,
    playbackRate,
    setOpen,
    setMinimized,
    setAudioUrl,
    setTitle,
    setIsPlaying,
    setPlaybackRate,
  }), [audioUrl, isPlaying, minimized, open, playbackRate, title]);

  return (
    <audioPlayerContext.Provider value={value}>
      {children}
    </audioPlayerContext.Provider>
  );
}

export function AudioPlayer({ showSkipBackAndForward = true }: AudioPlayerProps) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState([+(localStorage.getItem("volume") ?? 80)]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [waiting, setWaiting] = useState(false);

  const { open, title, audioUrl, isPlaying, setIsPlaying, setOpen, playbackRate } = useContext(audioPlayerContext);

  const deregisterAudioListenersRef = useRef<(() => void)>(undefined);

  const registerAudioRef = (ref: HTMLAudioElement) => {
    deregisterAudioListenersRef.current?.();
    const audio = audioRef.current = ref;
    if (!audio) {
      deregisterAudioListenersRef.current = undefined;
      return;
    }

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const stopPlaying = () => setIsPlaying(false);
    const startPlaying = () => {
      setWaiting(false);
      audio.playbackRate = playbackRate;
    };
    const setWaitingTrue = () => setWaiting(true);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', stopPlaying);
    audio.addEventListener('waiting', setWaitingTrue);
    audio.addEventListener('playing', startPlaying);

    deregisterAudioListenersRef.current = () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', stopPlaying);
      audio.removeEventListener('waiting', setWaitingTrue);
      audio.removeEventListener('playing', startPlaying);
    };
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume[0] / 100;
    }
    localStorage.setItem("volume", volume[0].toString());
  }, [volume]);

  // Makes sure that if it's already playing and the audioUrl changes,
  // the audio element starts playing the new audio.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) audio.play();
    else audio.pause();
  }, [audioUrl, isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = (value[0] / 100) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 15);
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(duration, audio.currentTime + 15);
  };

  if (!open) return <></>;

  return (
    <Card className="absolute bottom-0 left-0 w-dvw bg-gradient-card border-primary/20 shadow-lg z-50">
      <CardContent className="p-6">
        <audio
          ref={registerAudioRef}
          src={audioUrl}
          preload="metadata"
        />

        <div className="flex flex-row w-full gap-4">
          {/* Progress Bar */}
          <div className="grow">
            <Slider
              value={[duration ? (currentTime / duration) * 100 : 0]}
              onValueChange={handleSeek}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
          <XIcon
            className="relative -translate-y-1/2 translate-x-1/2"
            onClick={() => setOpen(false)}
          />
        </div>

        <div className="grid grid-cols-3">
          <div className="text-center items-center justify-center flex flex-row gap-4">
            <h3 className="text-lg font-semibold text-foreground">
              {title}
            </h3>
            {waiting && <Loader2 className="animate-spin" />}
          </div>
          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            {showSkipBackAndForward && <Button
              variant="outline"
              size="sm"
              onClick={skipBackward}
              className="rounded-full w-10 h-10 p-0"
            >
              <SkipBack className="w-4 h-4" />
            </Button>}

            <Button
              onClick={togglePlay}
              className="rounded-full w-12 h-12 p-0"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>

            {showSkipBackAndForward && <Button
              variant="outline"
              size="sm"
              onClick={skipForward}
              className="rounded-full w-10 h-10 p-0"
            >
              <SkipForward className="w-4 h-4" />
            </Button>}
          </div>

          {/* Currently, users are expected to use their device's audio controls */}
          {/* Volume Control */}
          {/* <div className="flex items-center gap-3 mb-4">
            <Volume2 className="w-4 h-4 text-muted-foreground" />
            <Slider
              value={volume}
              onValueChange={setVolume}
              max={100}
              step={1}
              className="flex-1"
            />
          </div> */}
        </div>
      </CardContent>
    </Card>
  );
}